import React, { createContext, useContext, ReactNode } from 'react';
import { useAzureAuth, UseAzureAuthReturn } from '@/hooks/useAzureAuth';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useOidcAuth, UseOidcAuthReturn } from '@/hooks/useOidcAuth';

// Re-export the Azure auth interface for convenience
export type AuthContextType = UseAzureAuthReturn | UseOidcAuthReturn;

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AzureAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const azureAuth = useAzureAuth();

  return (
    <AuthContext.Provider value={azureAuth}>
      {children}
    </AuthContext.Provider>
  );
};

const OidcAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const oidcAuth = useOidcAuth();

  return (
    <AuthContext.Provider value={oidcAuth}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const config = useAppConfig();

  if (config.authProvider === 'oidc') {
    return <OidcAuthProvider>{children}</OidcAuthProvider>;
  }

  return <AzureAuthProvider>{children}</AzureAuthProvider>;
};
